import React, { memo, useState, useEffect, useMemo, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { Panel, PanelHeader, PanelBody } from '../../components/panel/panel.jsx';
import PageContext from '../../config/page-settings.js';
import { Table, RowActionItems, RowItemView } from '../../components/table';
import * as API from '../../services/api';
import moment from 'moment';

const RequestInvestors = () => {
  const history = useHistory();
  const pageContext = useContext(PageContext);

  useEffect(() => {
    pageContext.handleSetPageSidebar(true);
    pageContext.handleSetPageHeader(true);
    pageContext.handleSetPageContentFullWidth(false);
  }, []);

  const [data, setData] = useState([]);
  const [skipPageReset, setSkipPageReset] = useState(false);

  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [meta, setMeta] = useState({});
  const [keyword, setKeyword] = useState('');
  const onChangeKeyword = (e) => {
    const keyword = e.target.value;
    setKeyword(keyword);
  };

  const getRequestParams = (keyword, page, pageSize) => {
    let params = {};

    if (keyword) {
      params['filter[search]'] = keyword;
    }

    if (page) {
      params['page'] = page;
    }

    if (pageSize) {
      params['size'] = pageSize;
    }

    params['filter[status]'] = 3;

    return params;
  };

  const [isLoading, setIsLoading] = useState(false);

  const fetchInvestors = () => {
    const params = getRequestParams(keyword, page, pageSize);
    setIsLoading(true);
    API.getInvestors(params)
      .then((response) => {
        const { data, meta } = response.data;
        setData(data);
        setMeta(meta);
        setIsLoading(false);
      })
      .catch((e) => {
        setData([]);
        setMeta({});
        setIsLoading(false);
      });
  };

  useEffect(fetchInvestors, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchInvestors();
  };

  const handlePageChange = (action) => (event, value) => {
    if (action === 'next') {
      setPage(page + 1);
    } else {
      setPage(page - 1);
    }
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setPage(1);
  };

  const columns = useMemo(
    () => [
      {
        header: 'Date Joined',
        accessorFn: (d) => {
          return moment(d.created_at).local().format('MMM DD, YYYY');
        },
        sortable: true
      },
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ cell }) => {
          const { original } = cell.row;
          return (
            <td role="cell" style={{ border: 0, padding: 0 }}>
              {original.first_name} {original.last_name}{' '}
              {original.is_qualified === 1 && (
                <span className="badge bg-yellow text-black">Qualified</span>
              )}
            </td>
          );
        },
        sortable: true
      },
      {
        header: 'Email',
        accessorKey: 'email',
        sortable: true
      },
      {
        header: 'Mobile',
        accessorFn: (d) => {
          return `${d.mobile_code}${d.mobile_number}`;
        },
        sortable: true
      },
      {
        header: 'Action Items',
        cell: ({ cell }) => {
          const { original } = cell.row;

          const onView = () => {
            history.push(`/investors/${original.id}`, original);
          };

          return (
            <RowActionItems>
              <RowItemView onClick={onView} />
            </RowActionItems>
          );
        }
      }
    ],
    []
  );

  const updateRow = (rowIndex, columnId, value) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);
    setData((prevData) =>
      prevData.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...prevData[rowIndex],
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  return (
    <>
      <h1 className="page-header">Investors</h1>
      <Panel>
        <PanelHeader>Pending Investors</PanelHeader>
        <PanelBody>
          <Table
            columns={columns}
            data={data}
            meta={meta}
            isLoading={isLoading}
            onSetPageSize={handlePageSizeChange}
            updateRow={updateRow}
            onNextPage={handlePageChange('next')}
            onPrevPage={handlePageChange('prev')}
            skipPageReset={skipPageReset}
            onChangeKeyword={onChangeKeyword}
            keyword={keyword}
            onSearchKeyword={handleSearch}
          />
        </PanelBody>
      </Panel>
    </>
  );
};

export default memo(RequestInvestors);
